/**
 * What Export reports about a trial (telemetry catalog §5.9): how export was set up, what changed, why a save
 * failed, why the export button was shown disabled, and how a Content Central connection failed. Pure functions
 * from the product's own state to catalog code words; nothing here reads a folder path, a file name, a naming,
 * a Content Central address or a key, and nothing it returns can carry one.
 */
import type { ExportJobSettings } from './desktop-api';

/** Where documents go, as the catalog says it (`kind`). */
export type ExportKind = 'folder' | 'content-central' | 'none';
export type ExportTriggerCode = ExportJobSettings['triggerMode'];

type SavedExport = Pick<
  ExportJobSettings,
  | 'triggerMode'
  | 'destinationKind'
  | 'destinationRoot'
  | 'destinationBindingId'
  | 'pdfProfile'
  | 'retentionDays'
  | 'localStoreRoot'
  | 'filingPlan'
  | 'enabled'
  | 'files'
  | 'exceptionsRoot'
>;

/** The destination's kind: a folder (with a folder chosen), Content Central, or none yet. */
export function exportKindOf(
  settings: Pick<ExportJobSettings, 'destinationKind' | 'destinationRoot'> | null | undefined,
): ExportKind {
  if (!settings) return 'none';
  if (settings.destinationKind === 'content-central') return 'content-central';
  return settings.destinationRoot ? 'folder' : 'none';
}

/** `export.saved`'s attributes: when documents leave and where they go. */
export function exportSavedAttrs(
  saved: Pick<ExportJobSettings, 'triggerMode' | 'destinationKind' | 'destinationRoot'>,
): { trigger: ExportTriggerCode; kind: ExportKind } {
  const trigger =
    saved.triggerMode === 'continuous' || saved.triggerMode === 'scheduled'
      ? saved.triggerMode
      : 'manual';
  return { trigger, kind: exportKindOf(saved) };
}

/** One `setting.changed` event: its full name and code-only attributes. */
export type ExportSettingChange = {
  name:
    | 'setting.changed:export-trigger'
    | 'setting.changed:export-naming'
    | 'setting.changed:export-retention'
    | 'setting.changed:export-destination'
    | 'setting.changed:export-archive-pdf'
    | 'setting.changed:export-backup-folder'
    | 'setting.changed:export-files'
    | 'setting.changed:export-exceptions-folder';
  attrs: {
    kind: 'chosen' | 'up' | 'down' | 'on' | 'off';
    choice?:
      | 'manual'
      | 'continuous'
      | 'scheduled'
      | 'folder'
      | 'content-central'
      | 'pdf-only'
      | 'pdf-data-text';
    count?: number;
    from: 'setup';
  };
};

/** Days a backup copy is kept, with Forever (null) the longest. */
const keptDays = (days: ExportJobSettings['retentionDays'] | undefined) =>
  days === null || days === undefined ? Number.POSITIVE_INFINITY : days;

/** Document types whose naming differs between two plans (added, removed or changed). Never the naming itself. */
function namingChanges(
  before: ExportJobSettings['filingPlan'] | undefined,
  after: ExportJobSettings['filingPlan'] | undefined,
): number {
  const rules = (plan: ExportJobSettings['filingPlan'] | undefined) =>
    new Map(
      (plan?.rules ?? []).map((rule) => [rule.documentTypeId, JSON.stringify(rule)] as const),
    );
  const was = rules(before),
    now = rules(after);
  let changed = 0;
  for (const [type, rule] of now) if (was.get(type) !== rule) changed++;
  for (const type of was.keys()) if (!now.has(type)) changed++;
  return changed;
}

/**
 * The export settings a save changed, as `setting.changed` events. Only a change to export that was already set up
 * counts: the first save (or a setup Content Central left switched off) is `export.saved`, not a list of changes.
 * Values are product words only (a trigger, a destination kind, up or down, on or off, a count of document types),
 * never the folder, the naming or the Content Central connection. The exceptions folder counts only when the person
 * chose one (`exceptionsChosen`: the save carried a picked folder), never when Save filled in the job's default.
 */
export function exportSettingChanges(
  previous: SavedExport | null | undefined,
  saved: SavedExport,
  options: { exceptionsChosen?: boolean } = {},
): ExportSettingChange[] {
  if (!previous?.enabled) return [];
  const changes: ExportSettingChange[] = [];
  const from = 'setup' as const;
  if (previous.triggerMode !== saved.triggerMode)
    changes.push({
      name: 'setting.changed:export-trigger',
      attrs: { kind: 'chosen', choice: exportSavedAttrs(saved).trigger, from },
    });
  const kindBefore = previous.destinationKind ?? 'filesystem',
    kindNow = saved.destinationKind ?? 'filesystem';
  const destinationChanged =
    kindBefore !== kindNow ||
    (kindNow === 'filesystem'
      ? previous.destinationRoot !== saved.destinationRoot
      : (previous.destinationBindingId ?? null) !== (saved.destinationBindingId ?? null));
  if (destinationChanged)
    changes.push({
      name: 'setting.changed:export-destination',
      attrs: {
        kind: 'chosen',
        choice: kindNow === 'content-central' ? 'content-central' : 'folder',
        from,
      },
    });
  const named = namingChanges(previous.filingPlan, saved.filingPlan);
  if (named > 0)
    changes.push({
      name: 'setting.changed:export-naming',
      attrs: { kind: 'chosen', count: named, from },
    });
  if (keptDays(previous.retentionDays) !== keptDays(saved.retentionDays))
    changes.push({
      name: 'setting.changed:export-retention',
      attrs: {
        kind: keptDays(saved.retentionDays) > keptDays(previous.retentionDays) ? 'up' : 'down',
        from,
      },
    });
  if (previous.pdfProfile !== saved.pdfProfile)
    changes.push({
      name: 'setting.changed:export-archive-pdf',
      attrs: { kind: saved.pdfProfile === 'pdfa-2u' ? 'on' : 'off', from },
    });
  if ((previous.localStoreRoot ?? null) !== (saved.localStoreRoot ?? null))
    changes.push({ name: 'setting.changed:export-backup-folder', attrs: { kind: 'chosen', from } });
  // ADR-0096: a folder export's Files (PDF only, or PDF + data + text files) and the exceptions folder, never its path.
  if (
    kindNow === 'filesystem' &&
    (previous.files ?? 'pdf-data-text') !== (saved.files ?? 'pdf-data-text')
  )
    changes.push({
      name: 'setting.changed:export-files',
      attrs: { kind: 'chosen', choice: saved.files ?? 'pdf-data-text', from },
    });
  if (
    options.exceptionsChosen &&
    saved.exceptionsRoot &&
    (previous.exceptionsRoot ?? null) !== saved.exceptionsRoot
  )
    changes.push({
      name: 'setting.changed:export-exceptions-folder',
      attrs: { kind: 'chosen', from },
    });
  return changes;
}

/** Why the export button was shown disabled (`export.blocked:<reason>`). */
export type ExportBlockedReason =
  | 'choose-folder'
  | 'finish-content-central'
  | 'retry-examples'
  | 'still-checking'
  | 'save-first'
  | 'paused'
  | 'job-not-ready'
  | 'other';

/**
 * Why Export's action is disabled right now, or null when it is not (or only for a moment: while a save or an export
 * runs, or because nothing is ready to send, which is not a hurdle). While export is off the action is the switch's
 * Turn on (owner decision, 2026-10-26; it was Start exporting): with no destination saved yet it saves what is shown,
 * so it waits for what that save waits for, in the order its hover says it ("Wait a moment", "Choose a folder first",
 * "Finish Content Central first", "Retry the examples first", "Still checking…"); with a destination saved it waits
 * only for an accepted Content Central setup to finish. Once export is on, it is Export now ("Save first" and so on).
 */
export function exportBlockedReason(state: {
  /** Export is on: the action is Export now; otherwise the switch's Turn on. */
  exportOn: boolean;
  /** The saved settings have a destination, so Turn on changes only on or off. */
  destinationSaved?: boolean;
  /** A save, an export or a load is running. */
  busy: boolean;
  /** An accepted Content Central setup is finishing. */
  operationLocked: boolean;
  destinationKind: 'filesystem' | 'content-central';
  /** A folder destination with no folder chosen. */
  folderMissing: boolean;
  /** Content Central's connection, catalog or matching is not finished. */
  contentCentralUnfinished: boolean;
  /** Content Central's connection or catalog needs attention (not only being checked). */
  contentCentralNeedsAttention: boolean;
  /** Content Central's catalog is being checked. */
  contentCentralChecking: boolean;
  /** A Content Central proposal waits to be used or dismissed. */
  proposalUnapplied: boolean;
  /** The folder examples' state. */
  examples: 'idle' | 'loading' | 'ready' | 'error';
  /** Export changes not saved yet. */
  unsaved: boolean;
  /** The job may export: it is active (its setup is finished and saved). */
  canRun: boolean;
}): ExportBlockedReason | null {
  if (state.busy) return null;
  if (!state.exportOn) {
    if (state.operationLocked) return 'still-checking';
    if (state.destinationSaved) return null;
    // While the catalog is being checked nothing can be said about it yet.
    if (state.destinationKind === 'content-central')
      return state.contentCentralChecking
        ? 'still-checking'
        : state.contentCentralUnfinished ||
            state.contentCentralNeedsAttention ||
            state.proposalUnapplied
          ? 'finish-content-central'
          : null;
    if (state.folderMissing) return 'choose-folder';
    if (state.examples === 'error') return 'retry-examples';
    return state.examples === 'ready' ? null : 'still-checking';
  }
  if (state.unsaved) return 'save-first';
  if (state.contentCentralNeedsAttention) return 'paused';
  if (state.operationLocked || state.contentCentralChecking) return 'still-checking';
  if (state.proposalUnapplied) return 'finish-content-central';
  if (!state.canRun) return 'job-not-ready';
  return null;
}

/** Why a save of export setup failed (`export.save-failed:<reason>`), and the code of the message shown for it. */
export type ExportSaveFailure = {
  reason: 'folder-not-prepared' | 'not-turned-on' | 'date-format' | 'other';
  code:
    | 'export-folder-not-prepared'
    | 'export-not-turned-on'
    | 'export-date-format'
    | 'export-save-failed';
};

/** Filing check codes that mean a date format the naming cannot use (FilingPlanEvaluator). */
const dateFormatCodes: ReadonlySet<string> = new Set(['date-format', 'format-kind']);

/**
 * A failed export save as code words: the default folder could not be prepared, the naming uses a date format it
 * cannot (from the filing check's codes, never its words), or the save itself failed, said as "export wasn't
 * turned on" while export was off and "changes weren't saved" once it was on.
 */
export function exportSaveFailure(input: {
  step: 'prepare-folder' | 'save';
  wasOn: boolean;
  namingErrorCodes?: readonly string[];
}): ExportSaveFailure {
  if (input.step === 'prepare-folder')
    return { reason: 'folder-not-prepared', code: 'export-folder-not-prepared' };
  if (input.namingErrorCodes?.some((code) => dateFormatCodes.has(code)))
    return { reason: 'date-format', code: 'export-date-format' };
  return input.wasOn
    ? { reason: 'other', code: 'export-save-failed' }
    : { reason: 'not-turned-on', code: 'export-not-turned-on' };
}

/** How a Content Central connect, check or key replacement failed (`content-central.failed:<kind>`). */
export type ContentCentralFailureKind =
  | 'unreachable'
  | 'certificate'
  | 'key-refused'
  | 'wrong-key-type'
  | 'timeout'
  | 'invalid-address'
  | 'other';

/**
 * The kind of a Content Central failure from the engine's own first sentence (ContentCentralFailure.cs), which is
 * fixed for each kind. Only the kind is kept; the sentence, the host it names and the technical detail are not.
 */
export function contentCentralFailureKind(reason: unknown): ContentCentralFailureKind {
  const message =
    reason instanceof Error ? reason.message : typeof reason === 'string' ? reason : '';
  const first = message.split('\n')[0] ?? '';
  if (/can[’']t reach content central/i.test(first)) return 'unreachable';
  if (/doesn[’']t trust content central[’']s security certificate/i.test(first))
    return 'certificate';
  if (/didn[’']t accept (this|the connection) key/i.test(first)) return 'key-refused';
  if (/didn[’']t answer in time|did not (answer|finish[^.]*) in time/i.test(first))
    return 'timeout';
  return 'other';
}
